<script lang="ts" setup>
import type { Agency } from '~/types/api'

interface AgencyCardProps {
    agency: Agency | undefined
    direction?: 'column' | 'horizontal'
    title?: string
    titleTag?: string
    imageSize?: string
}

const props = defineProps<AgencyCardProps>()

const $style = useCssModule()
const rootClasses = computed(() => {
    return [$style.root, props.direction && $style[`root--layout-${props.direction}`]]
})
</script>

<template>
    <section :class="rootClasses">
        <VRoundedPicture
            :entity="agency"
            :size="imageSize"
            :class="$style.image"
            :alt="agency?.title || ''"
        />
        <component
            :is="titleTag || 'div'"
            class="text-h4 cap-black-italic"
            :class="$style.title"
        >
            {{
                title || agency.title
            }}
        </component>
        <address :class="$style.address">
            <p v-if="agency?.location?.address && agency.location.address.addressLocality">
                {{ agency.location.address.streetAddress }}<br>
                {{ agency.location.address.postalCode }} {{ agency.location.address.addressLocality }}
            </p>
            <a
                v-if="agency?.telephone"
                class="bold"
                :href="`tel:${agency.telephone}`"
            >{{ agency.telephone }}</a>
        </address>
    </section>
</template>

<style lang="scss" module>
.root {
    display: grid;
    grid-template-columns: minmax(0, auto) 1fr;
    grid-template-rows: min-content;

    &--layout-horizontal {
        gap: 0 rem(20);
    }

    &--layout-column {
        grid-template-columns: 1fr;
    }
}

.image {
    grid-row: 1 / 3;

    .root--layout-horizontal & {
        grid-column: 2;
        justify-self: end;

        @include media('>=lg') {
            grid-column: inherit;
        }
    }

    .root--layout-column & {
        margin-bottom: rem(32);
    }
}

.title {
    margin-bottom: rem(8);

    .root--layout-column & {
        @include text-h2(18);

        margin-bottom: rem(16);
    }
}

.address {
    @include text-body-xs;

    font-style: inherit;

    .root--layout-column & {
        @include text-body-m;
    }
}
</style>
